import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/cookies/" }],
    meta: [
      { title: "Cookie Policy | RESTORE® Hair" },
      {
        name: "description",
        content:
          "How Restoration Holdings LLC (RESTORE® Hair) uses cookies and similar technologies, and how you can control them.",
      },
      { property: "og:title", content: "Cookie Policy — RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Learn how RESTORE® Hair uses cookies and similar tracking technologies and how to manage your preferences.",
      },
    ],
  }),
  component: CookiePolicyPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">{title}</h2>
      <div className="mt-3 space-y-4 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-background py-10 md:py-16">
          <div className="mx-auto max-w-3xl px-4">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Cookie Policy
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">April 15, 2024</p>

            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                This Cookie Policy explains how Restoration Holdings LLC ("Company," "we,"
                "us," and "our"), doing business as RESTORE® Hair, uses cookies and similar
                technologies to recognize you when you visit our website at{" "}
                <a
                  href="https://restorehair.com/"
                  className="text-primary underline"
                >
                  www.restorehair.com
                </a>{" "}
                ("Website"). It explains what these technologies are and why we use them, as
                well as your rights to control our use of them.
              </p>
              <p>
                In some cases we may use cookies to collect personal information, or that
                becomes personal information if we combine it with other information. In
                addition, this Cookie Policy describes the types of cookie information we may
                collect from you or that you may provide when you visit the Website and our
                practices for collecting, using, maintaining, protecting, and disclosing that
                information.
              </p>
              <p>This policy applies to information we collect:</p>
              <ul className="ml-6 list-disc space-y-2">
                <li>On this Website.</li>
                <li>In email, text, and other electronic messages between you and this Website.</li>
                <li>
                  Through mobile and desktop applications you download from this Website, which
                  provide dedicated non-browser-based interaction between you and this Website.
                </li>
                <li>
                  When you interact with our advertising and applications on third-party
                  websites and services, if those applications or advertising include links to
                  this policy.
                </li>
              </ul>
              <p>It does not apply to information collected by:</p>
              <ul className="ml-6 list-disc space-y-2">
                <li>
                  Us offline or through any other means, including on any other website
                  operated by Company or any third party (including our affiliates and
                  subsidiaries); or
                </li>
                <li>
                  Any third party (including our affiliates and subsidiaries) including through
                  any application or content (including advertising) that may link to or be
                  accessible from the Website.
                </li>
              </ul>
              <p>
                Please read the policy carefully to understand our policies and practices
                regarding your information and how we will treat it. If you do not agree with
                our policies and practices, your choice is not to use our Website. By
                accessing or using this Website, you agree to this privacy policy. This policy
                may change from time to time. Your continued use of this Website after we make
                changes is deemed to be acceptance of those changes, so please check the
                policy periodically for updates.
              </p>
            </div>

            <Section title="What are cookies?">
              <p>
                Cookies are small data files that are placed on your computer or mobile device
                when you visit a website. Cookies are widely used by website owners in order
                to make their websites work, or to work more efficiently, as well as to
                provide reporting information.
              </p>
              <p>
                Cookies set by the website owner (in this case, Restoration Holdings
                LLC/RESTORE Hair) are called "first-party cookies." Cookies set by parties
                other than the website owner are called "third-party cookies." Third-party
                cookies enable third-party features or functionality to be provided on or
                through the website (e.g., advertising, interactive content, and analytics).
                The parties that set these third-party cookies can recognize your computer
                both when it visits the website in question and also when it visits certain
                other websites.
              </p>
            </Section>

            <Section title="Why do we use cookies?">
              <p>
                We use first- and third-party cookies for several reasons. Some cookies are
                required for technical reasons in order for our Website to operate, and we
                refer to these as "essential" or "strictly necessary" cookies. Other cookies
                also enable us to track and target the interests of our users to enhance the
                experience on our Online Properties. Third parties serve cookies through our
                Website for advertising, analytics, and other purposes. This is described in
                more detail below. In addition, we use information that we collect about you
                or that you provide to us, including any personal information:
              </p>
              <ul className="ml-6 list-disc space-y-2">
                <li>To present our Website and its contents to you.</li>
                <li>To provide you with information, products, or services that you request from us.</li>
                <li>To fulfill any other purpose for which you provide it.</li>
                <li>To notify you about changes to our Website or any products or services we offer or provide through it.</li>
                <li>To allow you to participate in interactive features on our Website.</li>
                <li>In any other way we may describe when you provide the information.</li>
              </ul>
            </Section>

            <Section title="How can I control cookies?">
              <p>
                You have the right to decide whether to accept or reject cookies. You can
                exercise your cookie rights by setting your preferences in the Cookie Consent
                Manager. The Cookie Consent Manager allows you to select which categories of
                cookies you accept or reject. Essential cookies cannot be rejected as they are
                strictly necessary to provide you with services.
              </p>
              <p>
                The Cookie Consent Manager can be found in the notification banner and on our
                website. If you choose to reject cookies, you may still use our website though
                your access to some functionality and areas of our website may be restricted.
                You may also set or amend your web browser controls to accept or refuse
                cookies.
              </p>
              <p>
                The specific types of first- and third-party cookies served through our
                Website and the purposes they perform are described below (please note that
                the specific cookies served may vary depending on the specific Online
                Properties you visit):
              </p>
            </Section>

            <Section title="Essential website cookies">
              <ul className="ml-6 list-disc space-y-2">
                <li>
                  <span className="font-semibold text-foreground">cf_clearance:</span>{" "}
                  Cloudflare clearance cookie that stores the proof of challenge passed. It is
                  used to no longer issue a challenge if present. It is required to reach an
                  origin server.
                </li>
                <li>
                  <span className="font-semibold text-foreground">PHPSESSID:</span> PHP
                  language session cookie.
                </li>
                <li>
                  <span className="font-semibold text-foreground">NitroUX:</span> Temporary
                  storing core web vitals reported by the browser in case it can't be sent to
                  the endpoint. Nitropack.
                </li>
                <li>
                  <span className="font-semibold text-foreground">lastExternalReferrerTime:</span>{" "}
                  Detects how the user reached the website by registering their last URL
                  address.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Gcl_au:</span> Used by
                  Google AdSense for experimenting with advertisement efficiency across
                  websites using their services.
                </li>
                <li>
                  <span className="font-semibold text-foreground">_fbp:</span> Facebook
                  tracking pixel used to identify visitors for personalized advertising.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Gaconnector_all_traffic_sources:</span>{" "}
                  Google Analytics.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Calltrk-integration-data-ttl:</span>{" "}
                  Call tracking.
                </li>
              </ul>
            </Section>

            <Section title="How can I control cookies on my browser?">
              <p>
                As the means by which you can refuse cookies through your web browser
                controls vary from browser to browser, you should visit your browser's help
                menu for more information. You may refuse to accept browser cookies by
                activating the appropriate setting on your browser. However, if you select
                this setting you may be unable to access certain parts of our Website. Unless
                you have adjusted your browser setting so that it will refuse cookies, or
                opted out through the Cookie Consent Manager, our system will issue cookies
                when you direct your browser to our Website.
              </p>
              <p>The following is information about how to manage cookies on the most popular browsers:</p>
              <ul className="ml-6 list-disc space-y-2">
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://support.google.com/chrome/answer/95647">Chrome</a>
                </li>
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd">Internet Explorer</a>
                </li>
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop">Firefox</a>
                </li>
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://support.apple.com/en-ie/guide/safari/sfri11471/mac">Safari</a>
                </li>
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd">Edge</a>
                </li>
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://help.opera.com/en/latest/web-preferences/">Opera</a>
                </li>
              </ul>
              <p>
                In addition, most advertising networks offer you a way to opt out of targeted
                advertising. If you would like to find out more information, please visit:
              </p>
              <ul className="ml-6 list-disc space-y-2">
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://youradchoices.com/">Digital Advertising Alliance</a>
                </li>
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="https://youradchoices.ca/">Digital Advertising Alliance of Canada</a>
                </li>
                <li>
                  <a className="text-primary underline" target="_blank" rel="noopener" href="http://www.youronlinechoices.com/">European Interactive Digital Advertising Alliance</a>
                </li>
              </ul>
            </Section>

            <Section title="What about other tracking technologies, like web beacons?">
              <p>
                Cookies are not the only way to recognize or track visitors to a website. We
                may use other, similar technologies from time to time, like web beacons
                (sometimes called "tracking pixels" or "clear gifs"). These are tiny graphics
                files that contain a unique identifier that enables us to recognize when
                someone has visited our Website or opened an email including them. This
                allows us, for example, to monitor the traffic patterns of users from one
                page within a website to another, to deliver or communicate with cookies, to
                understand whether you have come to the website from an online advertisement
                displayed on a third-party website, to improve site performance, and to
                measure the success of email marketing campaigns. In many instances, these
                technologies are reliant on cookies to function properly, and so declining
                cookies will impair their functioning.
              </p>
            </Section>

            <Section title="Do you use Flash cookies or Local Shared Objects?">
              <p>
                Websites may also use so-called "Flash Cookies" (also known as Local Shared
                Objects or "LSOs") to, among other things, collect and store information
                about your use of our services, fraud prevention, and for other site
                operations.
              </p>
              <p>
                If you do not want Flash Cookies stored on your computer, you can adjust the
                settings of your Flash player to block Flash Cookies storage using the tools
                contained in the{" "}
                <a className="text-primary underline" target="_blank" rel="noopener" href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html">
                  Website Storage Settings Panel
                </a>
                . You can also control Flash Cookies by going to the{" "}
                <a className="text-primary underline" target="_blank" rel="noopener" href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager03.html">
                  Global Storage Settings Panel
                </a>{" "}
                and following the instructions.
              </p>
              <p>
                Please note that setting the Flash Player to restrict or limit acceptance of
                Flash Cookies may reduce or impede the functionality of some Flash
                applications, including, potentially, Flash applications used in connection
                with our services or online content.
              </p>
            </Section>

            <Section title="Do you serve targeted advertising?">
              <p>
                Third parties may serve cookies on your computer or mobile device to serve
                advertising through our Website. These companies may use information about
                your visits to this and other websites in order to provide relevant
                advertisements about goods and services that you may be interested in. They
                may also employ technology that is used to measure the effectiveness of
                advertisements. They can accomplish this by using cookies or web beacons to
                collect information about your visits to this and other sites in order to
                provide relevant advertisements about goods and services of potential
                interest to you. The information they collect may be associated with your
                personal information or they may collect information, including personal
                information, about your online activities over time and across different
                websites and other online services. They may use this information to provide
                you with interest-based (behavioral) advertising or other targeted content.
                We do not control these third parties' tracking technologies or how they may
                be used. If you have any questions about an advertisement or other targeted
                content, you should contact the responsible provider directly.
              </p>
            </Section>

            <Section title="Children Under the Age of 16">
              <p>
                Our website is not intended for children under 16 years of age. No one under
                age 16 may provide any personal information to or on the Website. We do not
                knowingly collect personal information from children under 16. If you are
                under 16, do not use or provide any information on this Website or through
                any of its features, register on the Website, make any purchases on the
                Website, use any of the interactive or public comment features of this
                Website, or provide any information about yourself to us, including your
                name, address, telephone number, email address, or any screen name or user
                name you may use. If we learn we have collected or received personal
                information from a child under 16 without verification of parental consent,
                we will delete that information. If you believe we might have any information
                from or about a child under 16, please contact us at{" "}
                <a className="text-primary underline" href="mailto:clientrelations@restorehair.com">
                  clientrelations@restorehair.com
                </a>
                .
              </p>
            </Section>

            <Section title="How often will you update this Cookie Policy?">
              <p>
                We may update this Cookie Policy from time to time in order to reflect, for
                example, changes to the cookies we use or for other operational, legal, or
                regulatory reasons. It is our policy to post any changes we make to our
                Cookie Policy on this page. If we make material changes, we will notify you
                through a notice on the Website home page. The date the Cookie Policy was
                last revised is identified at the top of the page. You are responsible for
                ensuring we have an up-to-date active and deliverable email address for you,
                and periodically visiting our Website and this Cookie Policy to check for any
                changes.
              </p>
              <p>The date at the top of this Cookie Policy indicates when it was last updated.</p>
            </Section>

            <Section title="Where can I get further information?">
              <p>
                If you have any questions about our use of cookies or other technologies,
                please email us at{" "}
                <a className="text-primary underline" href="mailto:clientrelations@restorehair.com">
                  clientrelations@restorehair.com
                </a>{" "}
                or by post to:
              </p>
              <address className="not-italic text-foreground">
                Restoration Holdings LLC<br />
                1415 W. 22nd Street<br />
                Oakbrook, IL 60523
              </address>
            </Section>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}